<script lang="ts">
export default {
    name: 'Pharmacies'
}
</script>

<script setup lang="ts">
import { Card } from '@/components/ui/card'
import { store } from '@/store/store';
import axios from 'axios'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from '@/components/ui/toast/use-toast';
import { SearchIcon, Loader, SquarePen } from 'lucide-vue-next';
import { clearLocalStorage, getParam } from '@/lib/utils'
import { refDebounced, debouncedRef } from '@vueuse/core'
import { usePatientFromRoute } from '@/composables/usePatientFromRoute'
import PharmacyName from '@/components/pharmacy-name.vue'
import PharmacyBadge from '@/components/pharmacy-badge.vue'
import { usePatientPostalCode } from '@/components/use-patient-postal-code';
import LocationDrawer from '@/components/LocationDrawer.vue';
import { trackEvent } from '@/utils/analytics';

const { toast } = useToast()
const route = useRoute();
const router = useRouter();
const { patientId } = usePatientFromRoute();

const pharmacies = ref([] as any);
const noPharmacies = ref(false);
const searchQuery = ref(route.query.keyword?.toString() || '')
const debouncedSearchQuery = refDebounced(searchQuery, 200)
const isSearching = ref(false)
const isUpdatingAppointment = ref(false);
const isSearchFieldDirty = ref(false);
const searchField = ref<HTMLElement | null>(null);

function choosePharmacy(index: number) {
    const selectedPharmacy = pharmacies.value[index];

    trackEvent('pharmacy_selected', {
        search_query_length: searchQuery.value.length,
        index,
        is_partner: selectedPharmacy.is_partner,
        is_preferred: selectedPharmacy.is_preferred,
    })
    
    store.appointment.preferredPharmacy = pharmacies.value[index];
    localStorage.setItem('appointment', JSON.stringify(store.appointment));
    if (localStorage.getItem('reschedule')) {
        if (isUpdatingAppointment.value) {
            return;
        }

        const clinicId = store.appointment.clinicID ? store.appointment.clinicID : store.clinic.id;

        const values = {
            doctorID: store.appointment.doctorID,
            time: store.appointment.time,
            day: store.appointment.day,
            appID: store.app.appID,
            pid: store.app.pid,
            cid: clinicId,
            preferredPharmacy: store.appointment.preferredPharmacy,
            save: true
        }
        isUpdatingAppointment.value = true;
        axios.post('/updtAppointment', values).then((res: any) => {
            if (res.status == 'success') {
                clearLocalStorage();
                router.replace({ name: 'success', query: { appID: res.data.appID } });
            } else {
                toast({
                    title: res.msg,
                    variant: 'destructive'
                })
            }
        }).finally(() => {
            isUpdatingAppointment.value = false;
        });
    } else {
        const query = { doctorName: store.selectedDoctor.name, day: store.appointment.day, cid: store.appointment.cid };
        // use new route with patient ID if available
        router.push({ name: 'patient_pay', query, params: { domain: getParam(route), patientId: patientId.value } });
    }
}

const { getPostalCodeFromServer,  } = usePatientPostalCode(patientId);

// location from query params
const queryPostalCode = computed(() => route.query.postalCode?.toString());
const queryLat = computed(() => route.query.lat ? parseFloat(route.query.lat.toString()) : null);
const queryLng = computed(() => route.query.lng ? parseFloat(route.query.lng.toString()) : null);

// determine display text for location
const locationDisplay = computed(() => {
    if (queryLat.value && queryLng.value) {
        return 'Your location';
    }

    if (queryPostalCode.value) {
        const code = queryPostalCode.value;
        if (code.length === 6) {
            return code.slice(0, 3) + ' ' + code.slice(3);
        }
        return code;
    }

    getPostalCodeFromServer();
    return 'Loading...';
});

// determine what to send to API
const searchLocation = computed(() => {
    if (queryLat.value && queryLng.value) {
        return {
            lat: queryLat.value,
            lng: queryLng.value
        };
    }
    if (queryPostalCode.value) {
        return {
            postal_code: queryPostalCode.value
        };
    }

    return {
        postal_code: undefined
    };
});

async function getPharmacies(query: string = '') {
    noPharmacies.value = false;
    isSearching.value = true;

    try {
        trackEvent('pharmacies_search_started', {
            search_query_length: query.length,
        })
        const pid = patientId.value || store.appointment.pid;

        // only fetch from server if no query params and no postal code
        if (!queryPostalCode.value && !queryLat.value) {
            await getPostalCodeFromServer();
        }

        const location = searchLocation.value;
        const res = await axios.post('/searchPharmacies', {
            cid: store.clinic.id,
            pid,
            keyword: query,
            ...location
        }) as any;

        if (res.status == 'success') {
            router.push({ query: { ...route.query, keyword: query } });
            pharmacies.value = res.data;
            if (pharmacies.value.length === 0) {
                noPharmacies.value = true;
            }
        }

    } catch (e) {
        console.error(e);
    } finally {
        isSearching.value = false;  
    }
}

watch(debouncedSearchQuery, (query) => {
    isSearchFieldDirty.value = true;

    getPharmacies(query);

    if (query.length === 0) {
        pharmacies.value = [];
        router.push({ query: { ...route.query, keyword: undefined } });
        noPharmacies.value = false;
    }
});

onMounted(() => {
    if (store.clinic.id) {
        getPharmacies();
    }
});

watch(() => [store.clinic.type, store.clinic.id], function () {
    getPharmacies();
});

// watch query params for location changes
watch([queryPostalCode, queryLat, queryLng], () => {
    if (store.clinic.id) {
        getPharmacies(searchQuery.value);
    }
});

const shouldShowSearchLoader = debouncedRef(isSearching, 150);
</script>

<template>
    <h2 class="title">Select your Preferred Pharmacy</h2>
    <div class="mt-2 text-gray-600"><span class="font-semibold">Search</span> and <span
            class="font-semibold">select</span> the pharmacy that you want to pickup your medication after the
        appointment.</div>

    <div class="relative pt-4" ref="searchField">
        <div class="relative">
            <input v-model="searchQuery" placeholder="Name, address or postal code"
                class="w-full p-2 pl-10 border border-gray-200 rounded-xl h-13 shadow-md outline-none focus:outline-4 focus:outline-white/20 focus:border-gray-300/80 focus:outline-offset-0 transition-all ease-in-out duration-100">
            <SearchIcon class="size-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />

            <div class="absolute top-1/2 right-3 transform -translate-y-1/2 text-foreground">
                <Loader :class="{
                    'size-5 animate-spin-slow': true,
                    'opacity-0': !shouldShowSearchLoader,
                    'opacity-100': shouldShowSearchLoader
                }" />
            </div>
        </div>
    </div>

    <div class="mt-4 flex flex-row gap-3 py-1 text-gray-500 font-medium justify-center items-center select-none">
        <span class="text-pretty text-sm sm:text-base font-semibold">Sorted by distance from</span>
        <LocationDrawer :current-postal-code="queryPostalCode || undefined">
            <Card
                class="text-sm text-gray-800 font-bold rounded-full px-2.5 py-0.5 leading-8 flex flex-row items-center justify-stretch gap-1 relative cursor-pointer shrink-0">
                <span>{{ locationDisplay }}</span>
                <span
                    class="h-full aspect-square p-1 w-8 border-l border-gray-200 flex items-center justify-center translate-x-1.5 shrink-0">
                    <SquarePen class="w-4 h-full" />
                </span>
            </Card>
        </LocationDrawer>
    </div>

    <div v-if="store.clinic.type == 'clinic' || store.clinic.type == 'group'" class="mt-4 flex flex-col gap-3">
        <Card v-for="(p, index) in pharmacies" :key="p.id"
            class="flex flex-row justify-between gap-1 cursor-pointer select-none rounded-xl"
            @click="choosePharmacy(index)">
            <div class="flex justify-between flex-col p-3 gap-1">
                <div class="font-semibold text-gray-900 capitalize text-normal">
                    <PharmacyName :name="p.name" />
                </div>
                <div v-if="p.postal_code || p.address" class="text-sm text-gray-500 tabular-nums font-medium">
                    {{ p.address }}, <span class="text-gray-400">{{ p.city }}</span>, {{p.postal_code }}
                </div>
                <div v-if="p.is_partner || p.is_preferred" class="flex flex-row gap-2 mt-2">
                    <PharmacyBadge v-if="p.is_preferred" kind="preferred" />
                    <PharmacyBadge v-if="p.is_partner" kind="partner" />
                </div>
            </div>
            <div v-if="p.bucket_label"
                class="text-xs text-gray-500 w-24 flex items-center justify-center border-l border-gray-200 font-bold flex-col gap-1 tabular-nums grow-0 shrink-0">
                <span class="text-gray-900 text-sm">{{ p.bucket_label }}</span>
                <span class="text-gray-400 font-normal">Distance</span>
            </div>
        </Card>
        <div v-if="noPharmacies && !isSearching" class="mt-10 text-balance text-center text-gray-500 font-medium px-12">
            We couldn't find
            any pharmacies that match your
            search.</div>
    </div>
</template>
